import { Inject, Injectable, Optional } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { delay, forkJoin, from, map, of, switchMap } from 'rxjs';
import {
  ApiSearch,
  StorageApiSearch,
} from 'src/app/api/services/api-utils.service';
import { FolderItem } from 'src/app/api/services/folder';
import { FoldersApiService } from 'src/app/api/services/folders-api.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import {
  FolderService,
  HasFolderStructure,
} from 'src/app/shared/services/folder.service';
import {
  BatchActionHandlerService,
  ListOperation,
  ListOperations,
} from './batch-action-handler.service';
import {
  ListAction,
  ListErrorHandlerService,
} from './list-error-handler.service';
import { ListSearchStateService } from './list-search-state.service';
import {
  ListItem,
  ScrollingListStateService,
} from './scrolling-list-state.service';
import {
  StorageFile,
  StorageFilesDetails,
  StorageUtilsService,
} from 'src/app/storage/services/storage-utils.service';
import { ListItemKind } from './scrolling-list-state.service';

@Injectable()
export class PaginatedScrollingListService<
  T extends { folderId?: string; id?: string },
> {
  list = toSignal(this.listState.list());
  activeOperation: ListOperation<T> | null;
  private readonly kind: ListItemKind;
  processFileApiResponse: (response: WithListFileResponse) => {
    items: ListItem<StorageFile>[];
    filesDetails: StorageFilesDetails;
    folders?: undefined;
    cursor?: undefined;
    nextPageToken?: undefined;
  };

  constructor(
    private readonly listState: ScrollingListStateService<T>,
    @Inject('WithListService')
    private readonly itemsService: WithList<T>,
    private readonly foldersService: FolderService,
    private readonly searchState: ListSearchStateService,
    @Inject('ListKind') kind: ListItemKind,
    private readonly listErrorHandler: ListErrorHandlerService,
    @Inject(BatchActionHandlerService)
    private readonly batchActionHandler: BatchActionHandlerService,
    private readonly userState: UserStateService,
    @Optional()
    @Inject('HasFolderStructure')
    private readonly folderStructureService: HasFolderStructure<T> | null,
  ) {
    this.kind = kind;
    this.searchState.init({
      folderKind: kind,
      query: undefined,
      includeSubcompanies: false,
      sortBy: 'changeDate',
      reverse: true,
      folderId: null,
      count: this.listState.maxCount,
    });
    this.listState.setName(kind + 's');
    this.init();
  }

  private init() {
    this.searchState
      .searchChanged()
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (search) => {
          if (this.listState.isLoadingItems()) return;
          this.listState.clear();
          this.updateList(search);
        },
      });
  }

  load(storageApiSearch?: StorageApiSearch) {
    this.updateList(
      storageApiSearch ? storageApiSearch : this.searchState.getSearchObject(),
    );
  }

  reload(storageApiSearch?: StorageApiSearch) {
    this.listState.clear();
    this.updateList(
      storageApiSearch ? storageApiSearch : this.searchState.getSearchObject(),
    );
  }

  setOperations(listOperations: ListOperations<T>) {
    if (!listOperations || !listOperations.operations?.length) {
      return;
    }
    this.batchActionHandler.init(listOperations);

    listOperations.operations.forEach((operation) => {
      if (operation.submenu && operation.children?.length) {
        operation.children.forEach((child) => {
          if (child.separator || child.uiOnly) {
            return;
          }
          this.updateSelectedAction(child);
        });
      } else if (!operation.submenu) {
        this.updateSelectedAction(operation);
      }
    });
  }

  private updateSelectedAction(operation: ListOperation<T>) {
    const execute = this.getExecuteAction(operation);

    operation.onClick = async (
      skipBeforeBatchAction?: boolean,
      selection?: ListItem<T>[],
      args?: any,
      storageApiSearch?: StorageApiSearch,
    ) => {
      let selected = selection || this.listState.getSelected();

      if (!selected.length) {
        return;
      }

      if (operation.filter) {
        selected = operation.deepFilter
          ? this.filterSelected(
              (await this.getItemsFromFolders(
                selected as ListItem<T>[],
              )) as ListItem<T>[],
              operation.filter,
            )
          : this.filterSelected(selected as ListItem<T>[], operation.filter);
      }
      let batchSelected: any[] = [...selected];

      if (operation.groupBy) {
        batchSelected = this.groupBySelected(batchSelected, operation.groupBy);
      }

      if (!batchSelected.length) {
        this.listState.setError({
          apiError:
            'None of the selected items can run this action. Check that they are online, meet the player requirements, and any other conditions for this operation.',
          errorMessage: 'No eligible items',
        });
        return;
      }

      if (!execute) {
        if (operation.beforeBatchAction && !skipBeforeBatchAction) {
          this.listState.setLoadingItems(true);
          try {
            await operation.beforeBatchAction(selected as ListItem<T>[]);
          } finally {
            this.listState.setLoadingItems(false);
          }
        }
        return;
      }

      this.listState.clearMessages();
      operation.inProgress = true;
      this.batchActionHandler
        .finished()
        .pipe(
          delay(2000), // Allow time for UI feedback before clearing operation state
        )
        .subscribe((finished) => {
          operation.inProgress = false;
          this.activeOperation = null;
          if (finished.success && operation.reloadList) {
            this.reload(storageApiSearch);
          } else if (
            finished.success === false &&
            !this.listState.currentError()
          ) {
            this.listState.setError({
              apiError: this.listErrorHandler.processErrorCode(
                this.listState.getName(),
                operation.name.toLowerCase() as ListAction,
                finished.error,
              ),
              errorMessage: finished.error?.message,
            });
          }
        });
      if (operation.beforeBatchAction && !skipBeforeBatchAction) {
        this.listState.setLoadingItems(true);
        operation
          .beforeBatchAction(selected as ListItem<T>[])
          .then((beforeArgs) => {
            this.activeOperation = operation;
            return this.batchActionHandler.batch(
              batchSelected,
              execute,
              operation,
              beforeArgs,
            );
          })
          .finally(() => this.listState.setLoadingItems(false));
      } else if (args) {
        this.activeOperation = operation;
        this.batchActionHandler.batch(batchSelected, execute, operation, args);
      } else {
        this.activeOperation = operation;
        this.batchActionHandler.batch(batchSelected, execute, operation);
      }
    };
  }

  private groupBySelected(
    selected: ListItem<T>[],
    groupBy: ListOperation<T>['groupBy'],
  ) {
    if (typeof groupBy === 'boolean') {
      return [{ items: selected.map((item) => item.item) }];
    }

    if (typeof groupBy === 'string') {
      const grouped = selected.reduce(
        (acc, item) => {
          const key = item.item[groupBy];
          if (!acc[key as string]) {
            acc[key as string] = [];
          }
          acc[key as string].push(item);
          return acc;
        },
        {} as Record<keyof (T | FolderItem), ListItem<T>[]>,
      );

      return Object.keys(grouped).map((key) => ({
        [groupBy]: key,
        items: grouped[key] as ListItem<T>[],
      }));
    }
  }

  private filterSelected(
    selected: ListItem<T>[],
    filter: (item: ListItem<T>) => boolean,
  ) {
    return selected.filter(filter);
  }

  private getExecuteAction(operation: ListOperation<T>) {
    if (!operation.actionCall) {
      return null;
    }

    const originalAction = operation.actionCall;

    return (item: any, args: unknown) => {
      return originalAction(item, args)
        .then(() => {
          if (operation.isDelete) {
            this.listState.remove(item);
          }
        })
        .catch((e) => {
          if (!this.listState.currentError()) {
            let apiError = e?.message;
            if (apiError) {
              if (
                e.category &&
                StorageUtilsService.STORAGE_CLIENT_ERROR[e.category]?.[apiError]
              ) {
                apiError =
                  StorageUtilsService.STORAGE_CLIENT_ERROR[e.category][
                    apiError
                  ];
              } else if (StorageUtilsService.STORAGE_CLIENT_ERROR[apiError]) {
                apiError = StorageUtilsService.STORAGE_CLIENT_ERROR[apiError];
              }
            }

            this.listState.setError({
              errorMessage: 'Something went wrong.',
              apiError,
              errorCategory: e?.category,
            });
          }
          throw e;
        });
    };
  }

  /**
   * Fetches one folder page and keeps only rows that actually render.
   *
   * A search is not scoped by parent, so a page can hold only folders from
   * other levels, all of which are dropped by `showFolderItem`. Each `load()`
   * still requests at most one folder page (size comes from the search count)
   * so a long run of invisible matches cannot keep the loading state open.
   * When that page is empty, `updateList()` falls through to items while
   * retaining the folder cursor for the next batch.
   */
  private async loadVisibleFolderPage(
    search: ApiSearch | StorageApiSearch,
    cursor: string | null,
  ): Promise<{ items: FolderItem[]; cursor: string | null }> {
    const page = await this.foldersService.loadFolderPage(
      search.folderKind,
      // `?? null` keeps the root case scoped to the root folder. Sending
      // `undefined` would drop the parentId clause and return folders from
      // every level, which pagination cannot account for.
      { ...search, folderId: search.folderId ?? null },
      cursor,
    );

    const items = page.items.filter(
      (folder) =>
        this.foldersService.showFolderItem(
          search.folderId || FoldersApiService.ROOT_FOLDER_ID,
          folder.parentId,
        ) ||
        (!search.folderId && search.query),
    );

    return { items, cursor: page.cursor };
  }

  /**
   * Loads the next batch. Folders paginate before items: a visible folder
   * page with a remaining cursor does not request items. Once folders run
   * out, or a folder page adds no visible rows, the same batch also brings a
   * full page of items — the item count is never reduced by how many folders
   * came along. Item rows are also post-filtered, so that fall-through does
   * not guarantee new list progress.
   *
   * The fall-through only applies while items remain. Folders can outlast the
   * item stream — every invisible folder page requests items, so items can run
   * out with the folder cursor still set. Requesting them again would send a
   * null items cursor and re-append the first page.
   */
  private updateList(search: ApiSearch | StorageApiSearch) {
    if (this.listState.isLoadingItems() || this.listState.isEndOfList()) return;
    this.listState.setLoadingItems(true);

    const foldersSupported =
      !this.userState.isAssignedPublisher() && this.kind !== 'file';
    this.listState.setFoldersEnabled(foldersSupported);

    const isFirstPage = this.listState.isFirstPage();
    const shouldLoadFolders =
      foldersSupported && !this.listState.isFoldersEnded();
    const shouldLoadItems = !this.listState.isItemsEnded();

    const folderPath$ =
      isFirstPage && foldersSupported
        ? from(this.foldersService.loadFolderPath(search.folderId))
        : of(undefined);

    const folderPage$ = shouldLoadFolders
      ? from(
          this.loadVisibleFolderPage(
            search,
            this.listState.currentFolderCursor(),
          ),
        )
      : of({ items: [] as FolderItem[], cursor: null });

    return forkJoin([folderPath$, folderPage$])
      .pipe(
        switchMap(([, folderPage]) =>
          (folderPage.items.length && folderPage.cursor) || !shouldLoadItems
            ? of({ folderPage, result: null })
            : from(
                this.itemsService.list(
                  this.kind === 'file' || !this.userState.isAssignedPublisher()
                    ? search
                    : this.getSearchForAssignedPublisher(search),
                  this.listState.currentCursor() ?? undefined,
                ),
              ).pipe(map((result) => ({ folderPage, result }))),
        ),
        map(({ folderPage, result }) => {
          if (this.kind === 'file') {
            if (!this.processFileApiResponse) {
              throw new Error(
                'processFileApiResponse must be set in the component',
              );
            }
            return {
              ...this.processFileApiResponse(result as WithListFileResponse),
              folders: [] as FolderItem[],
              folderCursor: null,
            };
          }

          const folders = folderPage.items;

          if (!result) {
            return {
              folders,
              folderCursor: folderPage.cursor,
              items: null,
              cursor: undefined,
              nextPageToken: undefined,
              filesDetails: undefined,
            };
          }

          return {
            folders,
            folderCursor: folderPage.cursor,
            items: this.userState.isAssignedPublisher()
              ? (result as WithListItemResponse<T>).items
              : (result as WithListItemResponse<T>).items.filter(
                  (item) =>
                    this.foldersService.showFolderItem(
                      search.folderId || FoldersApiService.ROOT_FOLDER_ID,
                      item.folderId,
                    ) ||
                    (!search.folderId && search.query),
                ),
            cursor: (result as WithListItemResponse<T>).cursor,
            nextPageToken: (result as WithListItemResponse<T>).nextPageToken,
            filesDetails: undefined,
          };
        }),
      )
      .subscribe({
        next: ({
          folders,
          folderCursor,
          items,
          cursor,
          nextPageToken,
          filesDetails,
        }) => {
          if (this.kind === 'file') {
            this.listState.add(items as ListItem<StorageFile>[], null);
            this.listState.filesDetails = filesDetails;
            return;
          }

          if (shouldLoadFolders) {
            this.listState.addFolders(
              folders.map((folder) => this.mapItem(folder)),
              folderCursor,
            );
          }

          // `items` is null while folders are still paginating; leaving the
          // items cursor untouched keeps the next batch on the same page.
          if (items) {
            this.listState.add(
              (items as T[]).map((item) => this.mapItem(item)),
              cursor || nextPageToken,
            );
          }
        },
        error: (e) => {
          this.listState.setError({
            apiError: this.listErrorHandler.processErrorCode(
              this.listState.getName(),
              'load',
              e,
            ),
            errorMessage: 'Failed to load ' + this.listState.getName() + '.',
          });
          this.listState.setLoadingItems(false);
        },
        complete: () => this.listState.setLoadingItems(false),
      });
  }

  private async getItemsFromFolders(listItems: ListItem<T>[]) {
    const folders = listItems.filter((item) => item.isFolder);
    const items = listItems
      .filter((item) => !item.isFolder)
      .map((item) => item.item as T);
    this.listState.setLoadingItems(true);
    const subfolders =
      folders.length > 0 && this.folderStructureService
        ? await Promise.all(
            folders.map((folder) =>
              this.foldersService.getItemsFromFolderAndSubfolders(
                folder.item.id,
                this.folderStructureService,
              ),
            ),
          )
        : [];
    this.listState.setLoadingItems(false);
    return [...items, ...subfolders.flat()].map((item) =>
      this.mapItem(item as T | FolderItem),
    );
  }

  mapItem(item: T | FolderItem) {
    return {
      item,
      isFolder: !!(item as FolderItem).isFolder,
      selected: false,
    } as ListItem<T>;
  }

  private getSearchForAssignedPublisher(search: ApiSearch) {
    const { folderId, ...rest } = search;
    return rest;
  }
}

export interface WithListItemResponse<T> {
  items?: T[];
  cursor: string | null;
  nextPageToken: string | null;
}

export interface WithListFileResponse extends StorageFilesDetails {
  files?: StorageFile[];
  serviceAssets?: any;
  result?: any;
}

export interface WithList<T> {
  list(
    apiSearch: ApiSearch,
    cursor?: string,
  ): Promise<WithListItemResponse<T> | WithListFileResponse>;
}
